import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Calculator, Search } from 'lucide-react';
import Seo from '../components/Seo';
import { BLOG_POSTS } from '../data/blogPosts';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const onSearch = (val) => {
    if (val) setSearchParams({ q: val });
    else setSearchParams({});
  };
  const term = q.trim().toLowerCase();
  const posts = term
    ? BLOG_POSTS.filter((p) =>
        `${p.title} ${p.description}`.toLowerCase().includes(term))
    : BLOG_POSTS;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'MoneyFeed Blog — Freelancer Tax & Bookkeeping Guides',
    url: 'https://moneyfeed.io/blog',
    blogPost: BLOG_POSTS.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      url: `https://moneyfeed.io/blog/${p.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]" data-testid="blog-page">
      <Seo
        title="Freelancer Tax & Bookkeeping Guides | MoneyFeed Blog"
        description="Practical guides on freelance taxes, Schedule C deductions, mileage, quarterly estimates and expense tracking for the self-employed."
        path="/blog"
        jsonLd={jsonLd}
      />

      <header className="flex items-center justify-between px-4 sm:px-8 py-4 max-w-6xl mx-auto">
        <Link to="/" className="flex items-center" data-testid="blog-logo-link">
          <img src="/logo-mark.png" alt="MoneyFeed" className="h-9 w-auto" />
        </Link>
        <Link to="/auth"><Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-5" data-testid="blog-signup-btn">Track it free</Button></Link>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 pb-16">
        <div className="text-center mb-10 mt-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] mb-3">The Freelancer Money Blog</h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">Simple, actionable guides to taxes and bookkeeping for freelancers, gig workers and the self-employed.</p>
          <Link to="/tools/1099-tax-calculator" className="inline-flex items-center gap-2 mt-4 text-[#064E3B] font-semibold hover:underline" data-testid="blog-calc-link">
            <Calculator className="h-4 w-4" /> Try the free 1099 tax calculator
          </Link>
          <div className="mt-6 max-w-md mx-auto relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={q}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search articles…"
              aria-label="Search blog articles"
              data-testid="blog-search-input"
              className="w-full rounded-full border border-[#064E3B]/20 bg-white pl-11 pr-4 py-3 text-sm text-[#064E3B] focus:outline-none focus:ring-2 focus:ring-[#84CC16]"
            />
          </div>
        </div>

        {posts.length === 0 && (
          <p className="text-center text-gray-500 pb-6" data-testid="blog-no-results">
            No articles match “{q}”. Try a different search.
          </p>
        )}
        <div className="space-y-4">
          {(posts.length ? posts : BLOG_POSTS).map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
              className="block bg-white rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all p-6 group">
              <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                <span>{new Date(p.date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                <span>·</span>
                <span>{p.readTime}</span>
              </div>
              <h2 className="text-xl font-bold text-[#064E3B] mb-2 group-hover:text-[#0a6b52]">{p.title}</h2>
              <p className="text-gray-600 text-sm mb-3">{p.description}</p>
              <span className="inline-flex items-center gap-1 text-[#84CC16] font-semibold text-sm">Read more <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
